/**
 * Tasks Page
 *
 * Displays user's tasks grouped by day with filtering and navigation support.
 * Feature: 078-user-tasks
 *
 * @module pages/TasksPage
 */

import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTasksGraphQL, useTaskSummaryGraphQL, type TaskSortOrder } from '@/hooks/graphql/useTasksGraphQL';
import {
  useUpdateTaskStatusGraphQL,
  useDeleteTaskGraphQL,
  type Task,
  type TaskStatus,
} from '@/hooks/graphql/useTaskMutationsGraphQL';
import { useAuth } from '@/services/auth';
import { useDevFlags } from '@/contexts/DevFlagsContext';
import { Heading } from '@/components/atoms/Heading';
import { Text } from '@/components/atoms/Text';
import { PageHeader, PageHeaderLabel } from '@/components/PageHeader';
import { TaskIcon } from '@/components/icons';
import { TaskGroup } from '@/components/tasks';
import { useTaskGrouping } from '@/hooks/useTaskGrouping';
import { useInfiniteScroll } from '@/hooks/useInfiniteScroll';
import { allStrategies } from '@/utils/task-grouping/grouping-strategies';
import logger from '@/utils/logger';

// Import from extracted modules
import type { FilterOption, DisplayItem } from './tasks/types';
import { formatDateHeader } from './tasks/utils';
import { TaskCard, TaskSummaryBanner } from './tasks/components';

// ============================================================================
// Main Component
// ============================================================================

export function TasksPage() {
  const { user } = useAuth();
  const devFlags = useDevFlags();
  const debugMode = devFlags?.flags.debugUI ?? false;
  const isAuthenticated = !!user;
  const [searchParams, setSearchParams] = useSearchParams();

  // Read filter and sort from URL params (with defaults)
  const filter = (searchParams.get('filter') as FilterOption) || 'pending';
  const sortOrder = (searchParams.get('sort') as TaskSortOrder) || 'NEWEST_FIRST';

  // Grouping strategy state - only critique actions enabled by default
  const [enabledStrategyIds, setEnabledStrategyIds] = useState<Set<string>>(
    () => new Set(['critique-action'])
  );
  const [isGroupingMenuOpen, setIsGroupingMenuOpen] = useState(false);
  const groupingMenuRef = useRef<HTMLDivElement>(null);

  // Get enabled strategies based on selected IDs
  const enabledStrategies = useMemo(
    () => allStrategies.filter(s => enabledStrategyIds.has(s.id)),
    [enabledStrategyIds]
  );

  // Toggle a strategy on/off
  const toggleStrategy = useCallback((strategyId: string) => {
    setEnabledStrategyIds(prev => {
      const next = new Set(prev);
      if (next.has(strategyId)) {
        next.delete(strategyId);
      } else {
        next.add(strategyId);
      }
      return next;
    });
  }, []);

  // Close grouping menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (groupingMenuRef.current && !groupingMenuRef.current.contains(e.target as Node)) {
        setIsGroupingMenuOpen(false);
      }
    };
    if (isGroupingMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isGroupingMenuOpen]);

  // Update URL params when filter or sort changes
  const setFilter = useCallback((newFilter: FilterOption) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (newFilter === 'pending') {
        next.delete('filter'); // Default value, don't clutter URL
      } else {
        next.set('filter', newFilter);
      }
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const setSortOrder = useCallback((newSort: TaskSortOrder) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (newSort === 'NEWEST_FIRST') {
        next.delete('sort'); // Default value, don't clutter URL
      } else {
        next.set('sort', newSort);
      }
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  // Build filters based on selection
  // Note: When viewing pending tasks, we also fetch completed tasks for grouping progress display
  const statusFilter = useMemo(() => {
    switch (filter) {
      case 'pending':
        // Include completed tasks so groups can show progress (e.g., "2 of 4 completed")
        return ['pending' as TaskStatus, 'completed' as TaskStatus];
      case 'completed':
        return ['completed' as TaskStatus];
      case 'dismissed':
        return ['dismissed' as TaskStatus];
      case 'all':
      default:
        return undefined;
    }
  }, [filter]);

  // Fetch tasks and summary
  const { tasks, hasMore, loading, error, loadMore } = useTasksGraphQL({
    filters: statusFilter ? { status: statusFilter } : undefined,
    sortOrder,
    skip: !isAuthenticated,
  });

  const { summary, loading: summaryLoading } = useTaskSummaryGraphQL(!isAuthenticated);

  // Mutation hooks
  const { updateStatus, loading: updatingStatus } = useUpdateTaskStatusGraphQL();
  const { deleteTask, loading: deleting } = useDeleteTaskGraphQL();

  const isUpdating = updatingStatus || deleting;

  // Infinite scroll for auto-loading more tasks
  const { sentinelRef } = useInfiniteScroll({
    onLoadMore: loadMore,
    hasMore,
    loading,
  });

  // Task grouping hook - groups critique tasks by source + action type
  const {
    visibleGroups,
    ungroupedTasks,
    isExpanded,
    toggleGroup,
  } = useTaskGrouping(tasks, enabledStrategies);

  // Filter ungrouped tasks based on current filter
  // (We fetch completed tasks for group progress display, but don't show them ungrouped when filter is 'pending')
  const filteredUngroupedTasks = useMemo(() => {
    if (filter === 'all') {
      return ungroupedTasks;
    }
    return ungroupedTasks.filter(task => task.status === filter);
  }, [ungroupedTasks, filter]);

  // Filter groups based on current filter
  // (Groups should only show if they have tasks matching the current filter)
  const filteredGroups = useMemo(() => {
    if (filter === 'all') {
      return visibleGroups;
    }
    return visibleGroups.filter(group => {
      switch (filter) {
        case 'pending':
          return group.pendingCount > 0;
        case 'completed':
          return group.completedCount > 0;
        case 'dismissed':
          return group.dismissedCount > 0;
        default:
          return true;
      }
    });
  }, [visibleGroups, filter]);

  const displayItems = useMemo((): DisplayItem[] => {
    const items: DisplayItem[] = [];

    // Add filtered groups
    for (const group of filteredGroups) {
      items.push({
        type: 'group',
        group,
        dateKey: group.latestCreatedAt.split('T')[0],
      });
    }

    // Add ungrouped tasks (already filtered by status)
    for (const task of filteredUngroupedTasks) {
      items.push({
        type: 'task',
        task,
        dateKey: task.createdAt.split('T')[0],
      });
    }

    // Sort by date (newest first by default)
    items.sort((a, b) => {
      const dateA = a.type === 'group' ? a.group.latestCreatedAt : a.task.createdAt;
      const dateB = b.type === 'group' ? b.group.latestCreatedAt : b.task.createdAt;
      return sortOrder === 'NEWEST_FIRST'
        ? dateB.localeCompare(dateA)
        : dateA.localeCompare(dateB);
    });

    return items;
  }, [filteredGroups, filteredUngroupedTasks, sortOrder]);

  // Group display items by date for section headers
  const displayItemsByDate = useMemo(() => {
    const grouped = new Map<string, DisplayItem[]>();
    for (const item of displayItems) {
      const existing = grouped.get(item.dateKey) || [];
      existing.push(item);
      grouped.set(item.dateKey, existing);
    }
    return grouped;
  }, [displayItems]);

  const sortedDates = useMemo(
    () => Array.from(displayItemsByDate.keys()).sort((a, b) =>
      sortOrder === 'NEWEST_FIRST' ? b.localeCompare(a) : a.localeCompare(b)
    ),
    [displayItemsByDate, sortOrder]
  );

  // Flatten all items for keyboard navigation
  const flatItems = useMemo(() => {
    const result: DisplayItem[] = [];
    for (const dateKey of sortedDates) {
      const dateItems = displayItemsByDate.get(dateKey) || [];
      result.push(...dateItems);
    }
    return result;
  }, [sortedDates, displayItemsByDate]);

  // Keyboard navigation state
  const [selectedIndex, setSelectedIndex] = useState<number>(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  // Reset selection when tasks change
  useEffect(() => {
    setSelectedIndex(-1);
  }, [filter, sortOrder]);

  // Get item key for ref registration
  const getItemKey = useCallback((item: DisplayItem): string => {
    return item.type === 'group' ? `group:${item.group.key}` : `task:${item.task.id}`;
  }, []);

  // Scroll selected item into view
  useEffect(() => {
    if (selectedIndex >= 0 && selectedIndex < flatItems.length) {
      const item = flatItems[selectedIndex];
      const key = getItemKey(item);
      const element = itemRefs.current.get(key);
      element?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [selectedIndex, flatItems, getItemKey]);

  // Keyboard event handler
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    // Ignore if in input/textarea
    if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
      case 'j':
        e.preventDefault();
        setSelectedIndex((prev) => {
          if (flatItems.length === 0) return -1;
          if (prev === -1) return 0;
          return Math.min(prev + 1, flatItems.length - 1);
        });
        break;

      case 'ArrowUp':
      case 'k':
        e.preventDefault();
        setSelectedIndex((prev) => {
          if (flatItems.length === 0) return -1;
          if (prev === -1) return flatItems.length - 1;
          return Math.max(prev - 1, 0);
        });
        break;

      case 'Enter':
        if (selectedIndex >= 0 && selectedIndex < flatItems.length) {
          e.preventDefault();
          const item = flatItems[selectedIndex];
          if (item.type === 'task') {
            logger.info('Tasks', 'Keyboard navigation to task', { taskId: item.task.id });
            window.location.href = item.task.targetUrl;
          } else {
            // For groups, toggle expand or navigate to group URL
            logger.info('Tasks', 'Keyboard navigation to group', { groupKey: item.group.key });
            window.location.href = item.group.url;
          }
        }
        break;

      case 'Escape':
        e.preventDefault();
        setSelectedIndex(-1);
        break;

      case 'Home':
        e.preventDefault();
        if (flatItems.length > 0) {
          setSelectedIndex(0);
        }
        break;

      case 'End':
        e.preventDefault();
        if (flatItems.length > 0) {
          setSelectedIndex(flatItems.length - 1);
        }
        break;
    }
  }, [flatItems, selectedIndex]);

  // Register item ref for scrolling (supports both tasks and groups)
  const registerItemRef = useCallback((itemKey: string, element: HTMLDivElement | null) => {
    if (element) {
      itemRefs.current.set(itemKey, element);
    } else {
      itemRefs.current.delete(itemKey);
    }
  }, []);

  // Handlers
  const handleComplete = async (taskId: string) => {
    try {
      await updateStatus(taskId, 'completed');
    } catch (err) {
      logger.error('Tasks', 'Failed to complete task', err);
    }
  };

  const handleDismiss = async (taskId: string) => {
    try {
      await updateStatus(taskId, 'dismissed');
    } catch (err) {
      logger.error('Tasks', 'Failed to dismiss task', err);
    }
  };

  const handleDelete = async (taskId: string) => {
    try {
      await deleteTask(taskId);
    } catch (err) {
      logger.error('Tasks', 'Failed to delete task', err);
    }
  };

  // Bulk action handlers for groups
  const handleCompleteAll = async (taskIds: string[]) => {
    logger.info('Tasks', 'Completing all tasks in group', { count: taskIds.length });
    const results = await Promise.allSettled(
      taskIds.map(taskId => updateStatus(taskId, 'completed'))
    );
    const failures = results.filter(r => r.status === 'rejected');
    if (failures.length > 0) {
      logger.error('Tasks', 'Some tasks failed to complete', {
        total: taskIds.length,
        failed: failures.length,
      });
    }
  };

  const handleDismissAll = async (taskIds: string[]) => {
    logger.info('Tasks', 'Dismissing all tasks in group', { count: taskIds.length });
    const results = await Promise.allSettled(
      taskIds.map(taskId => updateStatus(taskId, 'dismissed'))
    );
    const failures = results.filter(r => r.status === 'rejected');
    if (failures.length > 0) {
      logger.error('Tasks', 'Some tasks failed to dismiss', {
        total: taskIds.length,
        failed: failures.length,
      });
    }
  };

  // Navigate to task handler for group children
  const handleNavigateToTask = (task: Task) => {
    logger.info('Tasks', 'Navigating to task from group', { taskId: task.id, targetUrl: task.targetUrl });
  };

  // Not authenticated
  if (!isAuthenticated) {
    return (
      <div className="h-screen flex items-center justify-center">
        <div className="text-center">
          <Heading level="h2" className="mb-2">Sign in Required</Heading>
          <Text className="text-muted">
            Please sign in to view your tasks.
          </Text>
          <Link
            to="/login"
            className="mt-4 inline-block px-4 py-2 bg-primary text-primary-foreground rounded-lg"
          >
            Sign In
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="h-screen flex flex-col overflow-hidden outline-none"
      tabIndex={0}
      onKeyDown={handleKeyDown}
    >
      {/* Header */}
      <PageHeader>
        <PageHeaderLabel
          title="Tasks"
          icon={<TaskIcon size={32} />}
        />
        {!summaryLoading && (
          <TaskSummaryBanner
            pendingCount={summary.pendingCount}
            completedTodayCount={summary.completedTodayCount}
            dismissedCount={summary.dismissedCount}
          />
        )}
      </PageHeader>

      {/* Filter Tabs and Sort Controls */}
      <div className="flex-shrink-0 px-6 py-3 bg-surface-1 border-b border-border">
        <div className="flex items-center justify-between gap-4">
          {/* Filter Tabs */}
          <div className="flex gap-2">
            {(['pending', 'completed', 'dismissed', 'all'] as FilterOption[]).map((option) => (
              <button
                key={option}
                onClick={() => setFilter(option)}
                className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                  filter === option
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-surface-2 text-foreground hover:bg-surface-3'
                }`}
              >
                {option.charAt(0).toUpperCase() + option.slice(1)}
              </button>
            ))}
          </div>

          {/* Sort Controls */}
          <div className="flex items-center gap-4">
            {/* Grouping Strategy Dropdown */}
            <div className="relative" ref={groupingMenuRef}>
              <button
                onClick={() => setIsGroupingMenuOpen(!isGroupingMenuOpen)}
                className="flex items-center gap-2 px-3 py-1.5 text-sm bg-surface-2 border border-border rounded-lg text-foreground hover:bg-surface-3 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
                <span>Grouping</span>
                {enabledStrategyIds.size < allStrategies.length && (
                  <span className="ml-1 px-1.5 py-0.5 text-xs bg-primary/20 text-primary rounded">
                    {enabledStrategyIds.size}
                  </span>
                )}
                <svg className={`w-4 h-4 transition-transform ${isGroupingMenuOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {/* Dropdown Menu */}
              {isGroupingMenuOpen && (
                <div className="absolute right-0 mt-1 w-56 bg-surface-1 border border-border rounded-lg shadow-lg z-50">
                  <div className="p-2">
                    <div className="text-xs font-medium text-muted uppercase tracking-wide px-2 py-1 mb-1">
                      Active Groupings
                    </div>
                    {allStrategies.map((strategy) => (
                      <label
                        key={strategy.id}
                        className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-surface-2 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={enabledStrategyIds.has(strategy.id)}
                          onChange={() => toggleStrategy(strategy.id)}
                          className="w-4 h-4 rounded border-border text-primary focus:ring-primary focus:ring-offset-0"
                        />
                        <span className="text-sm text-foreground">{strategy.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Sort Dropdown */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted">Sort:</span>
              <select
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value as TaskSortOrder)}
                className="px-3 py-1.5 text-sm bg-surface-2 border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="NEWEST_FIRST">Newest First</option>
                <option value="OLDEST_FIRST">Oldest First</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="w-[75%] mx-auto p-4">
          {/* Loading State */}
          {loading && tasks.length === 0 && (
            <div className="text-center py-12">
              <Text className="text-muted">Loading tasks...</Text>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="p-4 bg-danger/10 border border-danger/30 rounded-lg">
              <Text className="text-danger">Failed to load tasks: {error.message}</Text>
            </div>
          )}

          {/* Empty State */}
          {!loading && !error && tasks.length === 0 && (
            <div className="text-center py-12">
              <Text className="text-muted text-lg mb-2">
                {filter === 'pending'
                  ? 'No pending tasks'
                  : filter === 'completed'
                  ? 'No completed tasks'
                  : filter === 'dismissed'
                  ? 'No dismissed tasks'
                  : 'No tasks found'}
              </Text>
              <Text className="text-muted text-sm">
                {filter === 'pending'
                  ? 'Create tasks during critique sessions or manually'
                  : 'Tasks will appear here when they match this filter'}
              </Text>
            </div>
          )}

          {/* Task List with Groups and Ungrouped Tasks by Date */}
          {sortedDates.map((dateKey) => {
            const dateItems = displayItemsByDate.get(dateKey) || [];
            return (
              <div key={dateKey} className="mb-6">
                <Heading level="h3" className="text-sm font-medium text-muted mb-3">
                  {formatDateHeader(dateKey)}
                </Heading>
                <div className="space-y-3">
                  {dateItems.map((item) => {
                    const itemIndex = flatItems.indexOf(item);
                    const isSelected = itemIndex === selectedIndex;

                    if (item.type === 'group') {
                      return (
                        <div
                          key={`group:${item.group.key}`}
                          ref={(el) => registerItemRef(`group:${item.group.key}`, el)}
                        >
                          <TaskGroup
                            group={item.group}
                            isExpanded={isExpanded(item.group.key)}
                            onToggle={() => toggleGroup(item.group.key)}
                            onCompleteAll={handleCompleteAll}
                            onDismissAll={handleDismissAll}
                            onNavigateToTask={handleNavigateToTask}
                            isFocused={isSelected}
                          />
                        </div>
                      );
                    }

                    return (
                      <TaskCard
                        key={item.task.id}
                        task={item.task}
                        onComplete={handleComplete}
                        onDismiss={handleDismiss}
                        onDelete={handleDelete}
                        isUpdating={isUpdating}
                        debugMode={debugMode}
                        isSelected={isSelected}
                        onRef={(el) => registerItemRef(`task:${item.task.id}`, el)}
                      />
                    );
                  })}
                </div>
              </div>
            );
          })}

          {/* Infinite scroll sentinel */}
          <div ref={sentinelRef} className="h-4" />
          {loading && hasMore && (
            <div className="text-center py-4">
              <Text className="text-muted">Loading more tasks...</Text>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
